import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  makeStyles,
  TextField,
  Chip,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Tooltip,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import DataTable, { TableColumn } from "react-data-table-component";
import { tableCustomStyles } from "../../utils/table-styles";
import PaginatedTableTop, { paginationTopClassName } from "../shared/paginated-table-top";
import DailyMetrics from "../shared/daily-metrics";
import TicketRangesPanel from "../shared/ticket-ranges-panel";
import { ApiResponse } from "apisauce";
import { toast } from "react-toastify";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import ReceiptIcon from "@material-ui/icons/Receipt";
import BusinessIcon from "@material-ui/icons/Business";
import InfoIcon from "@material-ui/icons/Info";
import { debounce } from "lodash";
import { Formik } from "formik";

import {
  useAppDispatch,
  useAppSelector,
  useTicketRangeLimits,
  shouldConfirmBatch,
} from "../../hooks";
import { BulkOperationMaxRange } from "../../utils/constants";
import { getCurrentUser } from "../../store/userStore";
import {
  getHydrovacTickets,
  getHydrovacPendingHeadspace,
  setHydrovacTickets,
  setHydrovacPendingHeadspace,
} from "../../store/hydrovacTicketStore";
import { HydrovacTicket } from "../../interface/api/HydrovacTicket";
import { ContainmentPad } from "../../interface/api/ContainmentPad";
import { SearchPayload } from "../../interface/api/SearchPayload";
import { ContractorCompaniesWithThresholdsResponse } from "../../interface/api/ContractorCompaniesWithThresholdsResponse";
import hydrovacTicketService from "../../services/hydrovacTicketService";
import contractorCompanyService from "../../services/contractorCompanyService";
import storageAreaService from "../../services/storageAreaService";
import ContainmentPadCell from "./containment-pad-cell";
import { history } from "../..";
import PageHero from "../shared/page-hero";
import SharedButton from "../shared/Button";
import PriceField from "../shared/price-field";
import InputSubtitle from "../shared/input-subtitle";
import { isAdmin, a11yProps, isScreeningFlagged } from "../../utils/constants";
import { TabPanel } from "../shared/tab-panel";
import {
  validateTicketRange,
  createDefaultSearchPayload,
  timeColumn,
  latestReceivedFirst,
} from "../../utils/ticket-helpers";
import { formatTicketNumber, bPrefix, hydrovacTicketNumber } from "../../utils/parser";
import { isDateEqualTo } from "../../utils/date-helper";
import { getCompactTruncateStyle } from "../../utils/table-helpers";
import TicketPill from "../shared/ticket-pill";
import useWindowSize from "../../hooks/useWindowSize";
import useFocusSearch from "../../hooks/useFocusSearch";
import {
  ActionIconGroup,
  StatusIconGroup,
  ReceivedIcon,
  PendingHeadspaceIcon,
  EditIcon,
  DigitalTicketIcon,
  TicketLogsIcon,
  ReceiveIcon,
  SoilFLOIcon,
  CheckIcon,
  ClearCheckIcon,
  ScreeningFlagIcon,
  SpinningRefreshIcon,
} from "../shared/icons";
import HydrovacReceiveDialog from "./hydrovac-receive-dialog";
import ConfirmGenerateTicketsDialog from "../shared/dialog/confirm-generate-tickets";
import GenerateTicketsProgressDialog from "../shared/dialog/generate-tickets-progress";
import HydrovacHeadspaceDialog from "./hydrovac-headspace-dialog";
import FormSelectSearchField from "../shared/form-select-search-field";
import ticketService from "../../services/ticketService";
import transportCompanyService from "../../services/transportCompanyService";
import { TransportCompany } from "../../interface/api/TransportCompany";
import { Load } from "../../interface/api/soilflo/Load";
import CheckBulkTicketsDialog from "../shared/dialog/check-bulk-tickets";
import { HydrovacBulkUpdatePayload } from "../../interface/api/HydrovacBulkUpdatePayload";
import { useBulkUpdateFlow } from "../../hooks/useBulkUpdateFlow";
import SoilFLOOverrideToast from "../shared/soilflo-override-toast";

// Persisted so the Regular/Tractor batch survives navigating away and back.
const BATCH_STORAGE_KEY = "hydrovacBatchIsBSeries";
// Static options for the branded batch selector (Regular vs Tractor/B-series).
const batchOptions = [
  { value: "regular", label: "Regular" },
  { value: "b", label: "Tractor" },
];

const useStyles = makeStyles({
  metricContainer: { padding: "15px", marginBottom: "10px" },
  metricText: { marginTop: "8px" },
});

const HydrovacDashboardPanel = () => {
  enum TabValues {
    Search,
    Generate,
    Export,
    Update,
    Clear,
    // Appended so the existing tab indices don't shift.
    Ranges,
  }

  const classes = useStyles();
  const [value, setValue] = useState(TabValues.Search);
  const user = useAppSelector(getCurrentUser);
  const tickets = useAppSelector(getHydrovacTickets);
  const pendingHeadspace = useAppSelector(getHydrovacPendingHeadspace);
  const dispatch = useAppDispatch();
  const { generateMax, exportMax } = useTicketRangeLimits();
  const [pending, setPending] = useState(false);
  // Range awaiting confirmation / currently generating; drives both dialogs.
  const [generateRange, setGenerateRange] = useState<{ from: number; to: number } | null>(null);
  const [confirmGenerateOpen, setConfirmGenerateOpen] = useState(false);
  // Bumped after a successful generate so the Ranges tab reflects it.
  const [rangesRefresh, setRangesRefresh] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTicketNumber, setSearchTicketNumber] = useState("");
  // Ticket batch the page's actions target: false = "Regular size" (numeric only),
  // true = "Tractor Trailer size" B-series (B-10001). Persisted so it survives
  // navigating away and back. Defaults to Regular.
  const [isBSeries, setIsBSeries] = useState(
    () => localStorage.getItem(BATCH_STORAGE_KEY) === "true"
  );
  const [generateFrom, setGenerateFrom] = useState("");
  const [generateTo, setGenerateTo] = useState("");
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receiveTicket, setReceiveTicket] = useState<HydrovacTicket | null>(null);
  const [soilFLOLoadData, setSoilFLOLoadData] = useState<Load | null>(null);
  const [isSoilFLOGenerate, setIsSoilFLOGenerate] = useState(false);
  const [headspaceDialogOpen, setHeadspaceDialogOpen] = useState(false);
  const [headspaceTicket, setHeadspaceTicket] = useState<HydrovacTicket | null>(null);
  const [showActive, setShowActive] = useState(false);
  const [showReceived, setShowReceived] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  // All completed tickets (all-time), fetched lazily when the Complete filter is
  // opened so it covers tickets outside the latest slice. null = not yet loaded.
  const [allComplete, setAllComplete] = useState<HydrovacTicket[] | null>(null);
  // All-time complete count for the chip, fetched up front (and on refresh) so
  // it is accurate before the full list is loaded. null = not yet loaded.
  const [completeCountValue, setCompleteCountValue] = useState<number | null>(null);
  // Today's active/received loads fetched server-side (full table, not the
  // latest-2000 slice) so the toggles and their counts are complete.
  const [activeTodayTickets, setActiveTodayTickets] = useState<HydrovacTicket[] | null>(null);
  const [receivedTodayTickets, setReceivedTodayTickets] = useState<HydrovacTicket[] | null>(null);
  const { isMobile, windowSize } = useWindowSize();
  const isCompact = Number(windowSize.width) <= 1200;
  const { doFocusSearchInput } = useFocusSearch("ticketNumber");

  // Contractors
  const [contractors, setContractors] = useState<
    { value: number; label: string }[]
  >([]);

  // Transport companies
  const [transportCompanies, setTransportCompanies] = useState<
    { value: number; label: string }[]
  >([]);

  // Containment pads (for the Containment Pad column — resolve id -> pad)
  const [containmentPadMap, setContainmentPadMap] = useState<
    Map<number, ContainmentPad>
  >(new Map());

  const {
    checkDialogOpen,
    checkResponse,
    onUpdate,
    onConfirmUpdate,
    onCloseCheckDialog,
  } = useBulkUpdateFlow<HydrovacBulkUpdatePayload>({
    service: hydrovacTicketService,
    buildPayload: (values) => ({
      from: Number(values.from),
      to: Number(values.to),
      isBSeries,
      contractorCompanyId: values.contractorCompanyId,
      contractorCompanyName: values.contractorCompanyName,
      propertyAddress: values.propertyAddress || null,
      landUse: values.landUse || null,
      shippingCompany: values.shippingCompany || null,
      transportCompanyId: values.transportCompanyId || null,
      shippingDriver: values.shippingDriver || null,
      salePrice: values.salePrice ? parseFloat(values.salePrice) : null,
      isSoilFLOLoad: values.isSoilFLOLoad,
      forceUpdate: values.forceUpdate,
    }),
    onSuccess: () => loadTickets(),
    setPending,
  });

  useEffect(() => {
    loadTickets();
    loadPendingHeadspace();
    loadContainmentPads();
    contractorCompanyService.getContractorCompanies().then((response) => {
      if (response.ok && response.data) {
        const data = response.data as ContractorCompaniesWithThresholdsResponse;
        const companies = data.contractorCompanies.filter(
          (c) => c.isHydrovac
        );
        setContractors(
          companies.map((c) => ({ value: c.id, label: c.name }))
        );
      }
    });
    transportCompanyService.getTransportCompanies().then((response) => {
      if (response.ok && response.data) {
        const companies = response.data as TransportCompany[];
        setTransportCompanies(
          companies.map((c) => ({ value: c.id, label: c.name }))
        );
      }
    });
  }, []);

  const loadTickets = async () => {
    setPending(true);
    setRefreshing(true);
    const [latest, active, received] = await Promise.all([
      hydrovacTicketService.getLatest(),
      hydrovacTicketService.getActiveToday(),
      hydrovacTicketService.getReceivedToday(),
    ]);
    if (latest.ok && latest.data) {
      dispatch(setHydrovacTickets({ tickets: latest.data as HydrovacTicket[] }));
    }
    if (active.ok && active.data) setActiveTodayTickets(active.data as HydrovacTicket[]);
    if (received.ok && received.data) setReceivedTodayTickets(received.data as HydrovacTicket[]);
    setPending(false);
    setRefreshing(false);
    // Refresh the complete chip count on every data change, and keep the
    // all-time complete list in sync if it's currently shown.
    loadCompleteCount();
    if (showComplete) loadAllComplete();
  };

  const loadCompleteCount = async (batch = isBSeries) => {
    const response: ApiResponse<any> = await hydrovacTicketService.getCompleteCount(batch);
    if (response.ok && response.data !== undefined) {
      setCompleteCountValue(response.data as number);
    }
  };

  const loadAllComplete = async () => {
    setPending(true);
    const response: ApiResponse<any> = await hydrovacTicketService.getAllComplete();
    if (response.ok && response.data) {
      setAllComplete(response.data as HydrovacTicket[]);
    }
    setPending(false);
  };

  // Fetch the full complete list whenever the Complete filter is opened.
  useEffect(() => {
    if (showComplete) loadAllComplete();
  }, [showComplete]);

  const loadPendingHeadspace = async () => {
    const response: ApiResponse<any> =
      await hydrovacTicketService.getPendingHeadspace();
    if (response.ok && response.data) {
      dispatch(setHydrovacPendingHeadspace({ tickets: response.data }));
    }
  };

  const loadContainmentPads = () => {
    storageAreaService.getContainmentPads().then((response) => {
      if (response.ok && response.data) {
        const pads = response.data as ContainmentPad[];
        setContainmentPadMap(new Map(pads.map((p) => [p.id, p])));
      }
    });
  };

  const onRefresh = () => {
    loadTickets();
    loadPendingHeadspace();
  };

  const searchTickets = async (payload: SearchPayload) => {
    setPending(true);
    const response: ApiResponse<any> =
      await hydrovacTicketService.searchTickets(payload);
    if (response.ok && response.data) {
      dispatch(setHydrovacTickets({ tickets: response.data }));
    }
    setPending(false);
  };

  const debouncedSearch = useCallback(
    debounce((val: string, isB: boolean) => {
      if (Number(val)) searchTickets(createDefaultSearchPayload(val, isB));
    }, 500),
    []
  );

  const onSearchTicketNumberChange = (val: string) => {
    setSearchTicketNumber(val);
    if (!val) {
      loadTickets();
      return;
    }
    debouncedSearch(val, isBSeries);
  };

  // Flipping the Standard/B toggle re-runs an active search against the new batch so
  // the correct 10001 vs B-10001 is shown; the full table is left untouched.
  const onToggleBSeries = (next: boolean) => {
    setIsBSeries(next);
    localStorage.setItem(BATCH_STORAGE_KEY, String(next));
    // Keep the all-time Complete count in sync with the selected batch.
    loadCompleteCount(next);
    if (searchTicketNumber && Number(searchTicketNumber)) {
      searchTickets(createDefaultSearchPayload(searchTicketNumber, next));
    }
  };

  // Validate, then either confirm or run straight away. runGenerate does the work
  // so the confirmation dialog can call it directly.
  const onGenerate = () => {
    const range = validateTicketRange(generateFrom, generateTo, generateMax);
    if (!range) return;
    setGenerateRange(range);
    if (shouldConfirmBatch(range.from, range.to)) {
      setConfirmGenerateOpen(true);
      return;
    }
    runGenerate(range);
  };

  const runGenerate = async (range: { from: number; to: number }) => {
    setConfirmGenerateOpen(false);
    setPending(true);
    const response: ApiResponse<any> =
      await hydrovacTicketService.generateTickets({
        from: range.from,
        to: range.to,
        isSoilFLOTicket: isSoilFLOGenerate,
        isBSeries,
        email: "",
      });
    if (response.ok && response.data) {
      if (response.data.success) {
        toast.success(`Tickets ${range.from}-${range.to} generated`);
        loadTickets();
        setRangesRefresh((n) => n + 1);
      } else {
        toast.error(response.data.error);
      }
    } else {
      toast.error("Failed to generate tickets");
    }
    setPending(false);
    setGenerateRange(null);
  };

  const onExport = async () => {
    const range = validateTicketRange(exportFrom, exportTo, exportMax);
    if (!range) return;

    const email = user ? user.username : "";
    if (!email) {
      toast.error("No email found for current user");
      return;
    }

    setPending(true);
    const response: ApiResponse<any> =
      await hydrovacTicketService.exportTicketsExcel({
        from: range.from,
        to: range.to,
        isBSeries,
        email,
      });
    if (response.ok) {
      toast.success(
        `Export queued — you'll receive it at ${email} when it's ready.`
      );
    } else {
      const data = response.data as string;
      toast.error(data || "Failed to export tickets");
    }
    setPending(false);
  };

  const onClear = async (values: any, resetForm: () => void) => {
    const range = validateTicketRange(values.from, values.to, BulkOperationMaxRange);
    if (!range) return;
    if (!values.clearAll && !values.clearContractor && !values.clearPropertyAddress && !values.clearLandUse && !values.clearShippingCompany && !values.clearShippingDriver && !values.clearSalePrice) {
      toast.error("Please select at least one field to clear");
      return;
    }
    setPending(true);
    const response: ApiResponse<any> =
      await hydrovacTicketService.bulkClear({
        from: range.from,
        to: range.to,
        isBSeries,
        clearAll: values.clearAll,
        clearContractor: values.clearContractor,
        clearPropertyAddress: values.clearPropertyAddress,
        clearLandUse: values.clearLandUse,
        clearShippingCompany: values.clearShippingCompany,
        clearShippingDriver: values.clearShippingDriver,
        clearSalePrice: values.clearSalePrice,
      });
    if (response.ok) {
      toast.success(`${response.data.cleared} ticket(s) cleared`);
      resetForm();
      loadTickets();
    } else {
      toast.error("Failed to clear tickets");
    }
    setPending(false);
  };

  const onReceiveClick = async (ticket: HydrovacTicket) => {
    // Fetch fresh ticket data
    const freshResponse = await hydrovacTicketService.getTicket(ticket.id);
    if (!freshResponse.ok) {
      toast.error("Failed to get latest ticket data");
      return;
    }
    const freshTicket = freshResponse.data as HydrovacTicket;

    if (freshTicket.isSoilFLOLoad) {
      ticketService.getLoad(hydrovacTicketNumber(freshTicket)).then((response) => {
        if (response.ok) {
          const data = response.data as Load[];
          if (data && data.length > 0) {
            const load = data.sort((a, b) => b.id - a.id)[0];
            setSoilFLOLoadData(load);

            try {
              const ticketWithSoilFLOData = { ...freshTicket };
              ticketWithSoilFLOData.propertyAddress = load.sourceSite?.name || "Unknown";
              ticketWithSoilFLOData.contractorCompanyName =
                load.sourceSite?.contractingcompany?.name || "Unknown";
              ticketWithSoilFLOData.shippingCompany =
                load.truck?.global_truck?.hauler?.name || "Unknown";
              ticketWithSoilFLOData.shippingDriver =
                load.truck?.driverName || "Unknown";
              ticketWithSoilFLOData.truckNumber =
                load.truck?.global_truck?.number || "Unknown";
              ticketWithSoilFLOData.truckLicensePlate =
                load.truck?.global_truck?.license || "Unknown";
              ticketWithSoilFLOData.timeLoaded = load.dispatchedTime;

              setReceiveTicket(ticketWithSoilFLOData);
              setReceiveDialogOpen(true);
            } catch (e) {
              toast.error("SoilFLO data is not correct");
            }
          } else {
            toast.error(
              <SoilFLOOverrideToast
                ticketNumber={freshTicket.ticketNumber}
                onOverride={() => {
                  setSoilFLOLoadData(null);
                  setReceiveTicket({ ...freshTicket, isSoilFLOLoad: false });
                  setReceiveDialogOpen(true);
                }}
              />,
              { autoClose: false }
            );
          }
        } else {
          toast.error("Failed to pull SoilFLO data");
        }
      });
    } else {
      setSoilFLOLoadData(null);
      setReceiveTicket(freshTicket);
      setReceiveDialogOpen(true);
    }
  };

  const onConfirmReceive = async (editedTicket: HydrovacTicket) => {
    const _ticket = { ...editedTicket };
    _ticket.timeUnloaded = null;
    const saveResponse = await hydrovacTicketService.saveTicket(_ticket);
    if (!saveResponse.ok) {
      toast.error("Failed to save ticket");
      return;
    }
    const response: ApiResponse<any> =
      await hydrovacTicketService.receiveTicket(editedTicket.id);
    if (!response.ok) {
      toast.error("Failed to receive ticket");
      return;
    }
    const data = response.data as any;
    if (data.success === false) {
      toast.error("Failed to update SoilFLO");
    } else {
      toast.success("Ticket received");
    }
    setReceiveDialogOpen(false);
    setReceiveTicket(null);
    setSoilFLOLoadData(null);
    setSearchTicketNumber("");
    loadTickets();
    loadPendingHeadspace();
    loadContainmentPads();
    doFocusSearchInput();
  };

  const onHeadspaceClick = (ticket: HydrovacTicket) => {
    setHeadspaceTicket(ticket);
    setHeadspaceDialogOpen(true);
  };

  const onResetHeadspace = async (ticket: HydrovacTicket) => {
    const blank = {
      ...ticket,
      headspaceReading: null,
      visualInspectionResult: null,
      inspect1_OrganicMaterial: null,
      inspect2_Insects: null,
      inspect3_UnusualOdours: null,
      inspect4_OtherMaterial: null,
      inspect5_HydrocarbonSheen: null,
      inspect6_HeadspaceAbove200: null,
    } as HydrovacTicket;
    const response: ApiResponse<any> =
      await hydrovacTicketService.clearHeadspace(ticket.id, blank);
    if (response.ok) {
      toast.success("Headspace data cleared — ticket set to pending");
      loadTickets();
      loadPendingHeadspace();
    } else {
      toast.error("Failed to reset headspace");
    }
  };

  const onConfirmEnterHeadspace = async (editedTicket: HydrovacTicket) => {
    const response: ApiResponse<any> =
      await hydrovacTicketService.clearHeadspace(editedTicket.id, editedTicket);
    if (response.ok) {
      toast.success("Headspace entered");
      setHeadspaceDialogOpen(false);
      setHeadspaceTicket(null);
      loadTickets();
      loadPendingHeadspace();
    } else {
      toast.error("Failed to enter headspace");
    }
  };


  // All totals below are scoped to the selected batch (Regular vs Tractor) so the
  // stat counts match the filtered table.
  const inBatch = (t: HydrovacTicket) => t.isBSeries === isBSeries;

  // Trucks loaded today (matches the other workstreams' "Loaded" metric), scoped
  // to the selected batch.
  const loadedToday = useMemo(
    () =>
      tickets
        .filter((t) => isDateEqualTo(t.timeLoaded, new Date()))
        .filter(inBatch).length,
    [tickets, isBSeries]
  );
  // Received shows tickets received today, regardless of the time-loaded date.
  // Hydrovac stamps the receive time on TimeUnloaded.
  const receivedCount = useMemo(
    () =>
      (receivedTodayTickets ??
        tickets.filter((t) => t.isReceived && isDateEqualTo(t.timeUnloaded, new Date())))
        .filter(inBatch).length,
    [receivedTodayTickets, tickets, isBSeries]
  );
  const uniqueContractors = useMemo(
    () =>
      new Set(
        tickets
          .filter(inBatch)
          .filter((t) => t.contractorCompanyName)
          .map((t) => t.contractorCompanyName)
      ).size,
    [tickets, isBSeries]
  );

  const activeCount = useMemo(
    () => {
      const todayActive =
        activeTodayTickets ??
        tickets.filter((t) => isDateEqualTo(t.timeLoaded, new Date()) && !t.isReceived);
      return [...todayActive, ...pendingHeadspace.filter((t) => !todayActive.some((a) => a.id === t.id))]
        .filter(inBatch).length;
    },
    [activeTodayTickets, tickets, pendingHeadspace, isBSeries]
  );

  // Complete shows all completed tickets regardless of date. Prefer the batch-aware
  // all-time count query; fall back to the fetched list, then the loaded slice.
  const completeCount = useMemo(
    () =>
      completeCountValue ??
      (allComplete ?? tickets.filter((t) => t.isComplete && !t.isPendingHeadspace))
        .filter(inBatch).length,
    [completeCountValue, allComplete, tickets, isBSeries]
  );

  const filteredTickets = useMemo(() => {
    let base: HydrovacTicket[];
    if (showActive) {
      const todayActive =
        activeTodayTickets ??
        tickets.filter((t) => isDateEqualTo(t.timeLoaded, new Date()) && !t.isReceived);
      base = [...todayActive, ...pendingHeadspace.filter((t) => !todayActive.some((a) => a.id === t.id))];
    } else if (showReceived) {
      base = latestReceivedFirst(
        receivedTodayTickets ??
          tickets.filter((t) => t.isReceived && isDateEqualTo(t.timeUnloaded, new Date())),
        (t) => t.timeUnloaded
      );
    } else if (showComplete) {
      base = allComplete ?? tickets.filter((t) => t.isComplete && !t.isPendingHeadspace);
    } else {
      base = tickets;
    }
    // The batch toggle shows one batch or the other — never a mix.
    return base.filter((t) => t.isBSeries === isBSeries);
  }, [tickets, allComplete, activeTodayTickets, receivedTodayTickets, pendingHeadspace, showActive, showReceived, showComplete, isBSeries]);

  const clearToggles = () => {
    setShowActive(false);
    setShowReceived(false);
    setShowComplete(false);
  };

  const onToggleShowActive = () => {
    const next = !showActive;
    clearToggles();
    setShowActive(next);
  };

  const onToggleShowReceived = () => {
    const next = !showReceived;
    clearToggles();
    setShowReceived(next);
  };

  const onToggleShowComplete = () => {
    const next = !showComplete;
    clearToggles();
    setShowComplete(next);
  };

  const truncateStyle = getCompactTruncateStyle(isCompact);

  // Shared Regular/Tractor batch selector (branded react-select). Full width on
  // mobile; fixed width beside the title on desktop.
  const renderBatchToggle = () => (
    <Box style={{ width: isMobile ? "100%" : 200 }}>
      <FormSelectSearchField
        name="hydrovacBatch"
        options={batchOptions}
        value={isBSeries ? batchOptions[1] : batchOptions[0]}
        onChange={(option: any) => onToggleBSeries(option?.value === "b")}
      />
    </Box>
  );

  const columns: TableColumn<HydrovacTicket>[] = [
    {
      name: "",
      width: isCompact ? "100px" : "125px",
      cell: (row) => {
        if (row.isPendingHeadspace) {
          return (
            <CheckIcon
              isMobile={isCompact}
              onClick={() => onHeadspaceClick(row)}
            />
          );
        }
        if (row.isReceived) return <></>;
        return (
          <ReceiveIcon
            isMobile={isCompact}
            onClick={() => onReceiveClick(row)}
          />
        );
      },
    },
    {
      name: "Ticket #",
      sortable: true,
      sortFunction: (a, b) => a.ticketNumber - b.ticketNumber,
      width: isCompact ? "110px" : "140px",
      cell: (row) => (
        <TicketPill
          ticketNumber={`[${row.prefix}] ${bPrefix(row)}${formatTicketNumber(row.ticketNumber)}`}
          copyValue={`${bPrefix(row)}${formatTicketNumber(row.ticketNumber)}`}
          isCompact={isCompact}
        />
      ),
    },
    showReceived || showComplete
      ? timeColumn<HydrovacTicket>("Time Received", (row) => row.timeUnloaded, isCompact)
      : timeColumn<HydrovacTicket>("Time Loaded", (row) => row.timeLoaded, isCompact),
    {
      name: "Property Address",
      selector: (row) => row.propertyAddress ?? "",
      sortable: true,
      maxWidth: isCompact ? "100px" : undefined,
      style: truncateStyle,
    },
    {
      name: "Contractor",
      selector: (row) => row.contractorCompanyName ?? "",
      sortable: true,
      maxWidth: isCompact ? "100px" : undefined,
      style: truncateStyle,
    },
    {
      name: "Containment Pad",
      width: isCompact ? "200px" : "280px",
      style: { paddingRight: 8 },
      cell: (row) => (
        <ContainmentPadCell
          pad={
            row.containmentPadId != null
              ? containmentPadMap.get(row.containmentPadId)
              : undefined
          }
        />
      ),
    },
    {
      name: "Bay",
      selector: (row) => row.containmentBay ?? "",
      sortable: true,
      width: isCompact ? "60px" : "80px",
    },
    {
      name: "Headspace",
      selector: (row) => row.headspaceReading ?? "",
      sortable: true,
      width: isCompact ? "80px" : "110px",
    },
    {
      name: "",
      width: "40px",
      cell: (row) => row.isSoilFLOLoad ? <SoilFLOIcon /> : null,
    },
    {
      name: "Status",
      width: "100px",
      cell: (row) => (
        <StatusIconGroup>
          {isScreeningFlagged(row) && <ScreeningFlagIcon />}
          {row.isPendingHeadspace && <PendingHeadspaceIcon />}
          {row.isComplete && <ReceivedIcon />}
          {row.isReceived && !row.isComplete && !row.isPendingHeadspace && <ReceivedIcon />}
        </StatusIconGroup>
      ),
    },
    {
      name: "Actions",
      width: isCompact ? "130px" : "150px",
      cell: (row) => (
        <ActionIconGroup>
          <EditIcon
            onClick={() => history.push(`/hydrovac/${row.id}`)}
          />
          <DigitalTicketIcon
            onClick={() =>
              history.push(`/hydrovac-digital-ticket/${row.id}`)
            }
          />
          <TicketLogsIcon
            onClick={() => history.push(`/hydrovac-ticket-log/${row.id}`)}
          />
          {row.isComplete && (
            <ClearCheckIcon onClick={() => onResetHeadspace(row)} />
          )}
        </ActionIconGroup>
      ),
    },
  ];

  return (
    <Container maxWidth="xl">
      <PageHero
        title="Hydrovac"
        subtitle="Manage hydrovac soil tickets with headspace tracking."
        showBackButton={false}
        action={renderBatchToggle()}
      />
      {isBSeries && (
        <Alert severity="info" style={{ marginBottom: 16 }}>
          Showing <strong>Tractor (B-series)</strong> tickets — all actions apply to the B batch.
        </Alert>
      )}
      <div>
        <AppBar position="static" color="default">
        <Tabs
          value={value}
          onChange={(_, newValue) => setValue(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="on"
          aria-label="scrollable tabs"
        >
          <Tab label="Search" {...a11yProps(0)} />
          {user &&
            isAdmin(user.roleId) && [
              <Tab key="generate" label="Generate" {...a11yProps(1)} />,
              <Tab key="export" label="Export" {...a11yProps(2)} />,
              <Tab key="update" label="Update" {...a11yProps(3)} />,
              <Tab key="clear" label="Clear" {...a11yProps(4)} />,
              <Tab key="ranges" label="Ranges" {...a11yProps(5)} />,
            ]}
        </Tabs>
        </AppBar>

        <TabPanel value={value} index={TabValues.Search}>
          <Box sx={{ marginY: 8 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  variant="outlined"
                  label="Ticket Number"
                  name="ticketNumber"
                  value={searchTicketNumber}
                  onChange={(e) =>
                    onSearchTicketNumberChange(e.target.value)
                  }
                  fullWidth
                  autoFocus
                />
                <InputSubtitle text="*Auto Search Enabled" />
              </Grid>
              <Grid item xs={12} md={4}>
                <SharedButton
                  label="Clear"
                  type="button"
                  variant="contained"
                  color="default"
                  onClick={() => {
                    setSearchTicketNumber("");
                    loadTickets();
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={value} index={TabValues.Generate}>
          <Box sx={{ marginY: 8 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="From Ticket Number"
                  value={generateFrom}
                  onChange={(e) => setGenerateFrom(e.target.value)}
                  variant="outlined"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="To Ticket Number"
                  value={generateTo}
                  onChange={(e) => setGenerateTo(e.target.value)}
                  variant="outlined"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSoilFLOGenerate}
                      onChange={(e) => setIsSoilFLOGenerate(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="SoilFLO Tickets"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SharedButton
                  label={pending ? "Generating..." : "Generate Tickets"}
                  type="button"
                  variant="contained"
                  color="primary"
                  onClick={onGenerate}
                  disabled={pending}
                />
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={value} index={TabValues.Export}>
          <Box sx={{ marginY: 8 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="From Ticket Number"
                  value={exportFrom}
                  onChange={(e) => setExportFrom(e.target.value)}
                  variant="outlined"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="To Ticket Number"
                  value={exportTo}
                  onChange={(e) => setExportTo(e.target.value)}
                  variant="outlined"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SharedButton
                  label={pending ? "Exporting..." : "Export"}
                  type="button"
                  variant="contained"
                  color="primary"
                  onClick={onExport}
                  disabled={pending}
                />
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={value} index={TabValues.Update}>
          <Box sx={{ marginY: 8 }}>
            <Formik
              initialValues={{
                from: "",
                to: "",
                contractorCompanyId: null as number | null,
                contractorCompanyName: null as string | null,
                propertyAddress: "",
                landUse: "",
                shippingCompany: "",
                transportCompanyId: null as number | null,
                shippingDriver: "",
                salePrice: "",
                isSoilFLOLoad: null as boolean | null,
                forceUpdate: false,
              }}
              onSubmit={(values, { resetForm }) => onUpdate(values, resetForm)}
            >
              {({ values, setFieldValue, handleSubmit }) => (
                <>
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="From Ticket Number"
                          value={values.from}
                          onChange={(e) => setFieldValue("from", e.target.value)}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="To Ticket Number"
                          value={values.to}
                          onChange={(e) => setFieldValue("to", e.target.value)}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormSelectSearchField
                          name="contractorCompanyId"
                          placeholder="Contractor"
                          options={contractors}
                          value={
                            values.contractorCompanyId
                              ? contractors.find((c) => c.value === values.contractorCompanyId) ?? null
                              : null
                          }
                          onChange={(option: any) => {
                            if (option) {
                              setFieldValue("contractorCompanyId", option.value);
                              setFieldValue("contractorCompanyName", option.label);
                            } else {
                              setFieldValue("contractorCompanyId", null);
                              setFieldValue("contractorCompanyName", null);
                            }
                          }}
                          isClearable
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="Property Address"
                          value={values.propertyAddress}
                          onChange={(e) => setFieldValue("propertyAddress", e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="Land Use"
                          value={values.landUse}
                          onChange={(e) => setFieldValue("landUse", e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormSelectSearchField
                          name="shippingCompany"
                          placeholder="Shipping Company"
                          options={transportCompanies}
                          value={
                            values.shippingCompany
                              ? transportCompanies.find((c) => c.label === values.shippingCompany) ?? null
                              : null
                          }
                          onChange={(option: any) => {
                            setFieldValue("shippingCompany", option ? option.label : null);
                            setFieldValue("transportCompanyId", option ? option.value : null);
                          }}
                          isClearable
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="Shipping Driver"
                          value={values.shippingDriver}
                          onChange={(e) => setFieldValue("shippingDriver", e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <PriceField
                          label="Sale Price"
                          value={values.salePrice}
                          onChange={(val) => setFieldValue("salePrice", val ?? "")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.isSoilFLOLoad === true}
                              onChange={(e) => setFieldValue("isSoilFLOLoad", e.target.checked ? true : null)}
                              color="primary"
                            />
                          }
                          label="SoilFLO"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        {/* Also updates received tickets, as the main stream's Force Update does. */}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.forceUpdate}
                              onChange={() => setFieldValue("forceUpdate", !values.forceUpdate)}
                              color="primary"
                            />
                          }
                          label="Force Update"
                        />
                      </Grid>
                    </Grid>
                    <Box mt={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <SharedButton
                            label={pending ? "Updating..." : "Update"}
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={pending}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </form>
                  <CheckBulkTicketsDialog
                    open={checkDialogOpen}
                    onClose={onCloseCheckDialog}
                    onConfirmSubmit={onConfirmUpdate}
                    response={checkResponse}
                  />
                </>
              )}
            </Formik>
          </Box>
        </TabPanel>

        <TabPanel value={value} index={TabValues.Clear}>
          <Box sx={{ marginY: 8 }}>
            <Formik
              initialValues={{
                from: "",
                to: "",
                clearAll: false,
                clearContractor: false,
                clearPropertyAddress: false,
                clearLandUse: false,
                clearShippingCompany: false,
                clearShippingDriver: false,
                clearSalePrice: false,
              }}
              onSubmit={(values, { resetForm }) => onClear(values, resetForm)}
            >
              {({ values, setFieldValue, handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="From Ticket Number"
                        value={values.from}
                        onChange={(e) => setFieldValue("from", e.target.value)}
                        variant="outlined"
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="To Ticket Number"
                        value={values.to}
                        onChange={(e) => setFieldValue("to", e.target.value)}
                        variant="outlined"
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box
                        style={{
                          border: "2px solid #ccc",
                          borderRadius: 4,
                          padding: "8px 16px",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.clearAll}
                              onChange={(e) => setFieldValue("clearAll", e.target.checked)}
                              color="primary"
                            />
                          }
                          label="Clear All"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.clearAll || values.clearContractor}
                            onChange={(e) => setFieldValue("clearContractor", e.target.checked)}
                            disabled={values.clearAll}
                            color="primary"
                          />
                        }
                        label="Contractor"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.clearAll || values.clearPropertyAddress}
                            onChange={(e) => setFieldValue("clearPropertyAddress", e.target.checked)}
                            disabled={values.clearAll}
                            color="primary"
                          />
                        }
                        label="Property Address"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.clearAll || values.clearLandUse}
                            onChange={(e) => setFieldValue("clearLandUse", e.target.checked)}
                            disabled={values.clearAll}
                            color="primary"
                          />
                        }
                        label="Land Use"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.clearAll || values.clearShippingCompany}
                            onChange={(e) => setFieldValue("clearShippingCompany", e.target.checked)}
                            disabled={values.clearAll}
                            color="primary"
                          />
                        }
                        label="Shipping Company"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.clearAll || values.clearShippingDriver}
                            onChange={(e) => setFieldValue("clearShippingDriver", e.target.checked)}
                            disabled={values.clearAll}
                            color="primary"
                          />
                        }
                        label="Shipping Driver"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.clearAll || values.clearSalePrice}
                            onChange={(e) => setFieldValue("clearSalePrice", e.target.checked)}
                            disabled={values.clearAll}
                            color="primary"
                          />
                        }
                        label="Sale Price"
                      />
                    </Grid>
                  </Grid>
                  <Box mt={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <SharedButton
                          label={pending ? "Clearing..." : "Clear"}
                          type="submit"
                          variant="contained"
                          color="primary"
                          disabled={pending}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </form>
              )}
            </Formik>
          </Box>
        </TabPanel>

        <TabPanel value={value} index={TabValues.Ranges}>
          <Box sx={{ marginY: 8 }}>
            <TicketRangesPanel
              stream="hydrovac"
              isBSeries={isBSeries}
              refreshToken={rangesRefresh}
              onUseRange={(from, to) => {
                setGenerateFrom(String(from));
                setGenerateTo(String(to));
                setValue(TabValues.Generate);
              }}
            />
          </Box>
        </TabPanel>
        {/* Daily metrics and the ticket listing are irrelevant on the Ranges tab. */}
        {value !== TabValues.Ranges && (
          <>
        <Paper variant="outlined" className={classes.metricContainer}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4} className={classes.metricText}>
              <Box display="flex" alignItems="center" mb={1}>
                <LocalShippingIcon style={{ marginRight: 6 }} />
                <Typography
                  variant="h6"
                  style={{ fontWeight: 600, marginRight: 6 }}
                >
                  Daily Trucks
                </Typography>
                <Tooltip title="Overview of today's hydrovac ticket activity and headspace status." placement="top" arrow>
                  <InfoIcon fontSize="small" color="action" style={{ cursor: "pointer" }} />
                </Tooltip>
              </Box>
              <DailyMetrics
                metrics={[
                  {
                    icon: <LocalShippingIcon fontSize="small" />,
                    value: loadedToday,
                    label: "Loaded",
                  },
                  {
                    icon: <ReceiptIcon fontSize="small" />,
                    value: receivedCount,
                    label: "Received",
                  },
                  {
                    icon: <BusinessIcon fontSize="small" />,
                    value: uniqueContractors,
                    label: "Contractors",
                  },
                ]}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Box display="flex" alignItems="center" justifyContent={isMobile ? "space-evenly" : "flex-end"} flexWrap="wrap" style={{ gap: 4, width: "100%" }}>
                  <Chip
                    label={`Active ${activeCount}`}
                    color={showActive ? "primary" : "default"}
                    onClick={onToggleShowActive}
                    variant={showActive ? "default" : "outlined"}
                    size="small"
                    style={isMobile ? { flex: "1 1 40%" } : undefined}
                  />
                  <Chip
                    label={`Received ${receivedCount}`}
                    color={showReceived ? "primary" : "default"}
                    onClick={onToggleShowReceived}
                    variant={showReceived ? "default" : "outlined"}
                    size="small"
                    style={isMobile ? { flex: "1 1 40%" } : undefined}
                  />
                  <Chip
                    label={`Complete ${completeCount}`}
                    color={showComplete ? "primary" : "default"}
                    onClick={onToggleShowComplete}
                    variant={showComplete ? "default" : "outlined"}
                    size="small"
                    style={isMobile ? { flex: "1 1 35%" } : undefined}
                  />
                  <SpinningRefreshIcon onClick={onRefresh} refreshing={refreshing} />
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <PaginatedTableTop>
          <DataTable
            className={paginationTopClassName}
            customStyles={tableCustomStyles}
            fixedHeader
            striped
            columns={columns}
            data={filteredTickets}
            pagination
            paginationPerPage={50}
            paginationRowsPerPageOptions={[50, 100, 150, 200]}
            progressPending={pending}
            progressComponent={<CircularProgress style={{ margin: 20 }} />}
            highlightOnHover
            responsive
          />
        </PaginatedTableTop>
          </>
        )}
      </div>
      {generateRange && (
        <ConfirmGenerateTicketsDialog
          open={confirmGenerateOpen}
          from={generateRange.from}
          to={generateRange.to}
          isBSeries={isBSeries}
          onClose={() => {
            setConfirmGenerateOpen(false);
            setGenerateRange(null);
          }}
          onConfirm={() => runGenerate(generateRange)}
        />
      )}
      {generateRange && (
        <GenerateTicketsProgressDialog
          open={pending && !confirmGenerateOpen}
          from={generateRange.from}
          to={generateRange.to}
          isBSeries={isBSeries}
        />
      )}
      <HydrovacReceiveDialog
        open={receiveDialogOpen}
        ticket={receiveTicket}
        onClose={() => {
          setReceiveDialogOpen(false);
          setReceiveTicket(null);
        }}
        onReceive={onConfirmReceive}
      />
      <HydrovacHeadspaceDialog
        open={headspaceDialogOpen}
        ticket={headspaceTicket}
        onClose={() => {
          setHeadspaceDialogOpen(false);
          setHeadspaceTicket(null);
        }}
        onEnterHeadspace={onConfirmEnterHeadspace}
      />
    </Container>
  );
};

export default HydrovacDashboardPanel;
